/**
 * i18n bootstrap — English + French (v1).
 * See DOCS_V4/00-canon/26-internationalization-and-localization.v1.md
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enCommon from '../../public/locales/en/common.json';
import enAuth from '../../public/locales/en/auth.json';
import enNav from '../../public/locales/en/nav.json';
import enTerminology from '../../public/locales/en/terminology.json';
import enErrors from '../../public/locales/en/errors.json';
import enPrioritization from '../../public/locales/en/prioritization.json';

import frCommon from '../../public/locales/fr/common.json';
import frAuth from '../../public/locales/fr/auth.json';
import frNav from '../../public/locales/fr/nav.json';
import frTerminology from '../../public/locales/fr/terminology.json';
import frErrors from '../../public/locales/fr/errors.json';
import frPrioritization from '../../public/locales/fr/prioritization.json';

export const SUPPORTED_LOCALES = ['en', 'fr'] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: AppLocale = 'en';
export const LOCALE_STORAGE_KEY = 'priorities.locale';

export const I18N_NAMESPACES = [
  'common',
  'auth',
  'nav',
  'terminology',
  'errors',
  'prioritization',
] as const;

export type I18nNamespace = (typeof I18N_NAMESPACES)[number];

function normalizeLocale(raw: string | null | undefined): AppLocale {
  if (!raw) return DEFAULT_LOCALE;
  const lower = raw.toLowerCase();
  if (lower.startsWith('fr')) return 'fr';
  if (lower.startsWith('en')) return 'en';
  return SUPPORTED_LOCALES.includes(lower as AppLocale) ? (lower as AppLocale) : DEFAULT_LOCALE;
}

export function syncDocumentLang(locale: AppLocale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
}

/** Apply locale from user preference (overrides detector). */
export async function applyLocale(locale: string | null | undefined): Promise<AppLocale> {
  const normalized = normalizeLocale(locale);
  if (i18n.language !== normalized) {
    await i18n.changeLanguage(normalized);
  }
  syncDocumentLang(normalized);
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
  } catch {
    // ignore storage failures
  }
  return normalized;
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: [...SUPPORTED_LOCALES],
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: 'common',
    ns: [...I18N_NAMESPACES],
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
      convertDetectedLanguage: (lng) => normalizeLocale(lng),
    },
    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        nav: enNav,
        terminology: enTerminology,
        errors: enErrors,
        prioritization: enPrioritization,
      },
      fr: {
        common: frCommon,
        auth: frAuth,
        nav: frNav,
        terminology: frTerminology,
        errors: frErrors,
        prioritization: frPrioritization,
      },
    },
  })
  .then(() => {
    syncDocumentLang(normalizeLocale(i18n.language));
    i18n.on('languageChanged', (lng) => syncDocumentLang(normalizeLocale(lng)));
  });

export default i18n;
